// @flow
import React, { Component } from 'react';
import type { RouterHistory } from 'react-router-dom';
import type { User } from 'gwu';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Grid from '@material-ui/core/Grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Player from '@vimeo/player';
import { white, brandColor } from '../../styles/style-constants';
import { remCalc, getPhoneMediaQueriedStyle } from '../../util/style-util';
import GwuHeader from './GwuHeader';
import landingPerson1 from '../../images/landing-person-1.png';
import landingPerson2 from '../../images/landing-person-2.png';
import landingPerson3 from '../../images/landing-person-3.png';
import videoExit from '../../images/video-exit.png';
import PathLineUtil from '../../util/path-line-util';
import { xPaddingDefault } from '../../styles/style-constants';
import Button from '../core/Button';
import Cookies from 'universal-cookie';
import { observer, inject } from 'mobx-react';

type Props = {
  lang: any,
  classes: Object,
  history: RouterHistory,
  userProfile: User,
  userLanguage: string,
  showLogout: boolean,
  onLogout: () => void,
  onViewOnBoarding: () => void,
  heroText: string,
  onPilotLaunch: () => void,
}

const BACKGROUND_IMAGES = [landingPerson1, landingPerson2, landingPerson3];

const vimeoUrl = videoCode => `https://player.vimeo.com/video/${videoCode}`;
const VIDEO_BY_LANGUAGE = {
  en: vimeoUrl(390270707),
  fr: vimeoUrl(390060086),
  es: vimeoUrl(390060135),
  ar: vimeoUrl(389817286),
  de: vimeoUrl(389821743),
  hi: vimeoUrl(389821791),
  id: vimeoUrl(389822859),
  it: vimeoUrl(389822231),
  ja: vimeoUrl(389822375),
  ko: vimeoUrl(389822409),
  zh: vimeoUrl(389822450),
  ru: vimeoUrl(389823040),
  th: vimeoUrl(389822563),
  tr: vimeoUrl(389822658)
};

class GwuLandingHeader extends Component<Props> {
  constructor(props) {
    super(props);
    this.state = {
      currentBackgroundImageIndex: 0,
      videoSeen: false,
      vimeoShow: false
    };
    this.handleToggleVideo = this.handleToggleVideo.bind(this);
    this.vimeoRef = React.createRef();
  }

  componentDidMount() {
    const self = this;
    window.addEventListener('resize', this.handleDrawLine);

    const cookies = new Cookies();
    if (!cookies.get('videoSeen')) {
      cookies.set('videoSeen', true, { path: '/' });
      const player = new Player(this.vimeoRef.current);
      self.handleToggleVideo();
      player.play();
    }

    this.initPersonImageAnimation();
  }

  initPersonImageAnimation() {
    const personDuration = 7000;

    const scrollImage = () => {
      const { currentBackgroundImageIndex } = this.state;
      const nextBackgroundImageIndex = (currentBackgroundImageIndex === BACKGROUND_IMAGES.length - 1) ? 0 : currentBackgroundImageIndex + 1;
      this.setState({
        currentBackgroundImageIndex: nextBackgroundImageIndex
      });
    };

    setInterval(scrollImage, personDuration);
  }

  renderHero = () => {
    const { heroText, onPilotLaunch, classes, lang } = this.props;

    const renderPilotButton = () => {
      if (!onPilotLaunch) {
        return null;
      }

      return (
        <div className={classes.heroPlayButton}>
          <Button
            color="landing" onClick={onPilotLaunch}
            size={'lg'}
          >
            <div className={classes.heroPlayButtonContents}>
              <span className={classes.heroPlayButtonText}>{lang.t('pilot.launch')}</span>
            </div>
          </Button>
        </div>
      );
    }
    return (
      <div className={classes.hero}>
        <Grid
          container spacing={32}
          alignItems={'flex-end'}
        >
          <Grid
            item sm={12}
            md={8}
          >
            <div>
              <span className={classes.heroText}>{heroText}</span>
            </div>
          </Grid>
          <Grid
            item sm={12}
            md={4}
          >
            <div className={classes.heroPlayButton}>
              <Button
                color="landing" onClick={this.handleToggleVideo}
                size={'lg'}
              >
                <div className={classes.heroPlayButtonContents}>
                  <span className={classes.heroPlayIcon}><FontAwesomeIcon icon="video" /></span>
                  <span className={classes.heroPlayButtonText}>{lang.t('home.hero.play')}</span>
                </div>
              </Button>
            </div>
            {renderPilotButton()}
          </Grid>
        </Grid>

      </div>
    );
  }

  renderVideoSection = () => {
    const self = this;
    const { userLanguage, classes, userStore: { isAnonymous } } = self.props;
    const { vimeoShow } = self.state;
    let videoUrl = VIDEO_BY_LANGUAGE[userLanguage] || VIDEO_BY_LANGUAGE['en'];
    if (isAnonymous) {
      videoUrl = vimeoUrl(651598759);
    }

    return (
      <div
        id="videoiFrame" className={classNames(classes.videoiFrame, !vimeoShow && classes.videoiFrameHidden)}
      >
        <iframe
          title="vimeo-player" ref={this.vimeoRef}
          src={`${videoUrl}?autoplay=0`}
          style={{ width: '100%', height: '99vh' }}
          frameBorder="0"
          allow="autoplay; fullscreen"
          webkitallowfullscreen="true"
          mozallowfullscreen="true"
          allowFullScreen
        />
        <div
          className={classes.videoiFrameCloseButton} onClick={() => { this.handleToggleVideo(); }}
        >
          <img
            className={classes.videoExit}
            src={videoExit}
            alt={'Close Video'}
          />
        </div>
      </div>
    );
  }

  handleToggleVideo = () => {
    const self = this;
    const player = new Player(this.vimeoRef.current);
    self.state.vimeoShow ? player.pause() : player.play();
    self.setState({ vimeoShow: !self.state.vimeoShow });
  }

  render() {
    const { classes, userProfile, history, showLogout, onLogout, onViewOnBoarding, lang } = this.props;
    const backgroundImageStyle = { backgroundImage: `url(${BACKGROUND_IMAGES[this.state.currentBackgroundImageIndex]})` };

    return (
      <div className={classes.container} style={backgroundImageStyle}>
        <GwuHeader
          userProfile={userProfile}
          history={history}
          showLogout={showLogout}
          onLogout={onLogout}
          onViewOnBoarding={onViewOnBoarding}
          omitBackground={true}
          lang={lang}
        />
        {PathLineUtil.renderAnchor('heroAnchor', true, 280)}
        {this.renderHero()}
        {this.renderVideoSection()}
      </div>
    );
  }
}

const styles = {
  container: {
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'right',
    backgroundOrigin: 'inherit',
    backgroundColor: brandColor,
    transition: 'background 2s ease-in-out'
  },
  hero: {
    padding: remCalc(230, xPaddingDefault, 135),
    ...getPhoneMediaQueriedStyle({
      padding: remCalc(75, 45)
    })
  },
  heroText: {
    fontFamily: 'DomaineDisplay-Bold',
    fontSize: remCalc(90),
    lineHeight: remCalc(100),
    color: white,
    ...getPhoneMediaQueriedStyle({
      fontSize: remCalc(45),
      lineHeight: remCalc(60)
    })
  },
  heroPlayButton: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: remCalc(30)
  },
  heroPlayButtonContents: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  heroPlayIcon: {
    fontSize: remCalc(21)
  },
  heroPlayButtonText: {
    marginLeft: remCalc(10)
  },
  videoiFrame: {
    width: '100%',
    height: 'auto',
    position: 'fixed',
    zIndex: '20000',
    left: 0,
    top: 0,
    background: 'rgba(0,0,0,.9)'
  },
  videoiFrameHidden: {
    display: 'none'
  },
  videoiFrameCloseButton: {
    color: '#ffffff',
    position: 'absolute',
    top: 10,
    right: 15,
    cursor: 'pointer',
    zindex: '20001',
    fontWeight: 800,
    textShadow: '2px 2px 8px #000000'
  }
};

export default withStyles(styles)(inject('userStore')(observer(GwuLandingHeader)));



// WEBPACK FOOTER //
// ./src/components/core/GwuLandingHeader.js